import React from "react";
import { useNavigate } from "react-router-dom";
import { GitBranch, ScanSearch, FileCode2, Rocket, PlusCircle, ShieldCheck, KeyRound, Repeat2 } from "lucide-react";

const STEPS = [
  {
    icon: GitBranch,
    title: "Submit Repository",
    description:
      "Provide your Git repository URL, target branch, and deployment target (GCP or Ubuntu VM). Private repositories are supported via SSH deploy keys.",
  },
  {
    icon: ScanSearch,
    title: "AI Analysis",
    description:
      "GoLive clones the repository and uses an LLM to analyse the codebase — detecting language, framework, dependencies, and infrastructure requirements.",
  },
  {
    icon: FileCode2,
    title: "Review & Generate Script",
    description:
      "A deployment proposal is generated for you to review and approve. Once approved, GoLive generates a ready-to-run deployment script tailored to your stack.",
  },
  {
    icon: Rocket,
    title: "Download & Deploy",
    description:
      "Download the generated deployment script and run it on your target environment at your own pace. The script is fully self-contained and ready to execute.",
  },
];

const HIGHLIGHTS = [
  {
    icon: ShieldCheck,
    title: "Human-in-the-loop",
    description: "Every deployment proposal requires a human review and approval before a script is generated — nothing is produced without sign-off.",
  },
  {
    icon: KeyRound,
    title: "Secure by default",
    description: "Private repositories use ephemeral Ed25519 SSH deploy keys scoped to a single repo. Keys are encrypted at rest.",
  },
  {
    icon: Repeat2,
    title: "Full audit trail",
    description: "Every run captures who submitted, who approved, which model was used, and what was deployed — ready for compliance reviews.",
  },
];

export default function GoLiveHomePage() {
  const navigate = useNavigate();

  return (
    <div className="gl-page max-w-4xl">
      {/* Hero */}
      <div className="mb-10">
        <h1 className="text-3xl font-bold text-slate-900 mb-3">Orion GoLive</h1>
        <p className="text-base text-slate-500 leading-relaxed max-w-2xl">
          GoLive is an AI-powered deployment pipeline that takes your Git repository from source code
          and automatically analyses your stack, then generates ready-to-run deployment scripts
          tailored to your target infrastructure.
        </p>
        <button
          type="button"
          onClick={() => navigate("/golive/new")}
          className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-blue-500 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
        >
          <PlusCircle size={16} />
          New Deployment
        </button>
      </div>

      {/* Pipeline steps */}
      <div className="mb-10">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-4">How it works</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {STEPS.map(({ icon: Icon, title, description }, i) => (
            <div key={title} className="bg-white border border-gray-200 rounded-xl p-5 flex gap-4">
              <div className="shrink-0 flex flex-col items-center gap-1">
                <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                  <Icon size={16} className="text-blue-600" />
                </div>
                <span className="text-[10px] font-bold text-slate-300">0{i + 1}</span>
              </div>
              <div>
                <p className="font-semibold text-slate-800 text-sm mb-1">{title}</p>
                <p className="text-xs text-slate-500 leading-relaxed">{description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Highlights */}
      <div>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-4">Key principles</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {HIGHLIGHTS.map(({ icon: Icon, title, description }) => (
            <div key={title} className="bg-white border border-gray-200 rounded-xl p-5">
              <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center mb-3">
                <Icon size={16} className="text-slate-600" />
              </div>
              <p className="font-semibold text-slate-800 text-sm mb-1">{title}</p>
              <p className="text-xs text-slate-500 leading-relaxed">{description}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
